import { API_BASE_URL } from "@/lib/api/constants";

const QUEUE_STORAGE_KEY = "alraheem:offline-queue";
const QUEUE_EVENT = "alraheem:offline-queue-change";
const isBrowser = typeof window !== "undefined";

export interface QueuedRequest {
  id: string;
  endpoint: string;
  method: string;
  body?: string | null;
  headers?: Record<string, string>;
  timestamp: number;
  attempts: number;
  lastError?: string;
}

const readQueue = (): QueuedRequest[] => {
  if (!isBrowser) {
    return [];
  }

  try {
    const raw = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as QueuedRequest[];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("[offline-queue] Failed to read queue", error);
    return [];
  }
};

let cachedQueue: QueuedRequest[] = readQueue();

const setCache = (queue: QueuedRequest[]) => {
  cachedQueue = queue;
};

const writeQueue = (queue: QueuedRequest[]) => {
  if (!isBrowser) {
    return;
  }

  try {
    setCache(queue);
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
    const event = new CustomEvent(QUEUE_EVENT, { detail: { queue } });
    window.dispatchEvent(event);
  } catch (error) {
    console.warn("[offline-queue] Failed to write queue", error);
  }
};

export const getOfflineQueueSnapshot = (): QueuedRequest[] => cachedQueue;

export const subscribeOfflineQueue = (callback: () => void) => {
  if (!isBrowser) {
    return () => void 0;
  }

  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<{ queue?: QueuedRequest[] }>;
    if (customEvent.detail?.queue) {
      setCache(customEvent.detail.queue);
    } else {
      setCache(readQueue());
    }
    callback();
  };
  window.addEventListener(QUEUE_EVENT, handler);
  return () => window.removeEventListener(QUEUE_EVENT, handler);
};

export const enqueueRequest = (payload: Omit<QueuedRequest, "id" | "timestamp" | "attempts">) => {
  if (!isBrowser) {
    return null;
  }
  const queue = readQueue();
  const item: QueuedRequest = {
    ...payload,
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    attempts: 0,
  };
  queue.push(item);
  writeQueue(queue);
  return item;
};

const sendQueuedRequest = async (req: QueuedRequest) => {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(req.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(`${API_BASE_URL}${req.endpoint}`, {
    method: req.method,
    headers,
    body: req.body ?? undefined,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || "Request failed");
  }
};

let isProcessing = false;

export const processOfflineQueue = async () => {
  if (!isBrowser || isProcessing) {
    return { synced: 0, remaining: getOfflineQueueSnapshot().length };
  }

  if (!navigator.onLine) {
    return { synced: 0, remaining: getOfflineQueueSnapshot().length };
  }

  const queue = readQueue();
  if (queue.length === 0) {
    return { synced: 0, remaining: 0 };
  }

  isProcessing = true;
  let synced = 0;
  const remaining: QueuedRequest[] = [];

  for (const item of queue) {
    try {
      await sendQueuedRequest(item);
      synced += 1;
    } catch (error) {
      console.warn("[offline-queue] Failed to replay request", error);
      remaining.push({
        ...item,
        attempts: item.attempts + 1,
        lastError: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  writeQueue(remaining);
  isProcessing = false;

  return { synced, remaining: remaining.length };
};

export const clearOfflineQueue = () => {
  if (!isBrowser) {
    return;
  }
  writeQueue([]);
};


